import { useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import { useEmployeesT } from './kit';

const BASE = 'https://media.easy-peasy.ai/landing/ai-employees/v1/';
const FILM_1080 = `${BASE}film-1080.mp4`;
const FILM_720 = `${BASE}film-720.mp4`;
const POSTER = `${BASE}film-poster-1920.webp`;
const POSTER_SMALL = `${BASE}film-poster-960.webp`;

// Where each part of the film starts, in seconds (the film's own timeline).
const CHAPTERS = [
  { at: 8, label: 'Hire' },
  { at: 12, label: 'Brief' },
  { at: 18, label: 'Tasks' },
  { at: 22, label: 'Approvals' },
  { at: 26.4, label: 'Routines' },
];

const clock = (seconds: number) =>
  `0:${String(Math.floor(seconds)).padStart(2, '0')}`;

/** The 38-second launch film: loads nothing until someone presses play. */
export default function EmployeesFilm() {
  const { t } = useEmployeesT();
  const video = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const [showing, setShowing] = useState(false);
  // Safari ignores a seek before the file's metadata has loaded.
  const pendingSeek = useRef<number | null>(null);

  const play = (from?: number) => {
    const node = video.current;
    if (!node) return;
    setStarted(true);
    if (from !== undefined) {
      if (node.readyState >= 1) node.currentTime = from;
      else pendingSeek.current = from;
    }
    void node.play().catch(() => setStarted(false));
  };

  return (
    <section id='film' className='scroll-mt-8 pb-10 pt-20 sm:pb-14 sm:pt-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          align='center'
          title={t('One launch, start to finish.')}
          tail={t('In 38 seconds.')}
          lead={t(
            'Watch a team of AI employees take a product launch from one brief to a banner, last spring’s numbers and an approved email.',
          )}
        />
      </div>

      <div className='mx-auto mt-12 max-w-6xl px-3 sm:mt-16 sm:px-6 lg:px-8'>
        <div className='rounded-[26px] bg-[#eeeefc] p-2 ring-1 ring-inset ring-[#0f0f28]/[0.05] sm:rounded-[34px] sm:p-3 dark:bg-[#15142a] dark:ring-white/[0.06]'>
          <div className='relative overflow-hidden rounded-[20px] bg-[#f1f1ee] shadow-[0_40px_90px_-40px_rgba(22,12,90,0.55)] sm:rounded-[26px]'>
            <video
              ref={video}
              preload='none'
              playsInline
              controls={started}
              onLoadedMetadata={(event) => {
                if (pendingSeek.current === null) return;
                event.currentTarget.currentTime = pendingSeek.current;
                pendingSeek.current = null;
              }}
              onPlaying={() => setShowing(true)}
              aria-label={t('AI Employees launch film')}
              className='block aspect-video w-full bg-[#f1f1ee]'
            >
              <source
                src={FILM_1080}
                type='video/mp4'
                media='(min-width: 768px)'
              />
              <source src={FILM_720} type='video/mp4' />
            </video>

            {/* The poster is an image (not the video's own poster) so phones
                load the small one. It stays until the first frame plays. */}
            <div
              className={clsxm(
                'absolute inset-0 transition-opacity duration-500',
                showing && 'pointer-events-none opacity-0',
              )}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={POSTER}
                srcSet={`${POSTER_SMALL} 960w, ${POSTER} 1920w`}
                sizes='(min-width: 1152px) 1100px, 100vw'
                alt=''
                loading='lazy'
                decoding='async'
                className='h-full w-full object-cover'
              />
              <button
                type='button'
                onClick={() => play()}
                className='group absolute inset-0 flex items-center justify-center focus-visible:outline-none'
                aria-label={t('Play the film')}
              >
                <span className='flex items-center gap-3 rounded-full bg-[#10101c]/90 py-2.5 pe-5 ps-2.5 text-[15px] font-semibold text-white shadow-[0_20px_50px_-15px_rgba(16,16,28,0.7)] transition duration-300 group-hover:scale-[1.04] group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-4 group-focus-visible:outline-primary-600'>
                  <span className='flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#10101c]'>
                    {started ? (
                      <span className='h-4 w-4 animate-spin rounded-full border-2 border-[#10101c]/25 border-t-[#10101c]' />
                    ) : (
                      <svg
                        viewBox='0 0 20 20'
                        fill='currentColor'
                        className='ms-0.5 h-4 w-4 rtl:-scale-x-100'
                        aria-hidden='true'
                      >
                        <path d='M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z' />
                      </svg>
                    )}
                  </span>
                  {t('Play the film')}
                  <span className='font-medium text-white/60' dir='ltr'>
                    0:38
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>

        <ul className='mt-6 flex flex-wrap items-center justify-center gap-2'>
          {CHAPTERS.map((chapter) => (
            <li key={chapter.label}>
              <button
                type='button'
                onClick={() => play(chapter.at)}
                className={clsxm(
                  'flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-[13px] font-semibold transition hover:ring-[#0f0f28]/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-white/[0.06] dark:hover:ring-white/30',
                  tone.line,
                  tone.ink,
                )}
              >
                <span className={clsxm('tabular-nums', tone.quiet)} dir='ltr'>
                  {clock(chapter.at)}
                </span>
                {t(chapter.label)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
